<template>
  <v-menu
    v-model="appsMenu"
    transition="slide-y-transition"
  >
    <template #activator="{ props }">
      <IconButton
        v-bind="props"
        class="navigation-menu__toggle-btn ml-2"
        icon="mdi:apps"
        :icon-size="24"
        :size="32"
      />
    </template>

    <NavList class="app-menu">
      <template
        v-for="group in appGroups"
        :key="group.name"
      >
        <v-list-item
          v-if="group.name"
          class="app-menu-item--subsection text-h4"
          :title="group.name"
        />

        <template
          v-for="app in group.apps"
          :key="app.slug"
        >
          <v-list-item
            v-if="app.external_link"
            v-bind="commonListItemProps"
            :href="app.external_link"
            :target="app.open_in_new_tab === false ? '' : '_blank'"
            :title="app.menu_name || app.name"
          >
            <template #prepend>
              <vi-icon :icon="app.icon" />
            </template>

            <template
              v-if="app.append_link"
              #append
            >
              <a
                class="app-menu-item__append-link"
                :href="app.append_link.external_link"
                target="_blank"
                @click.stop=""
              >
                <IconButton
                  :icon="app.append_link.icon"
                  :ripple="true"
                  :size="32"
                  tabindex="-1"
                  variant="plain"
                />
              </a>
            </template>

            <template
              v-else-if="app.open_in_new_tab !== false"
              #append
            >
              <vi-icon icon="mdi:open-in-new" />
            </template>
          </v-list-item>

          <v-list-item
            v-else
            v-bind="commonListItemProps"
            :title="app.menu_name || app.name"
            :to="`/${app.slug}`"
          >
            <template #prepend>
              <vi-icon :icon="app.icon" />
            </template>
          </v-list-item>
        </template>
      </template>
    </NavList>
  </v-menu>
</template>

<script setup lang="ts">
const isAuthenticated = useIsAuthenticated();
const apps = useApps();
const appsMenu = ref(false);

const appGroups = useAsyncData<AppGroup[]>('userApps', async () => {
  if (!isAuthenticated.value) {
    // Avoid even trying to access this if the user isn't authenticated
    // and we need to sign them out on the client
    return [];
  }
  await apps.fetch();
  return apps.grouped;
}, {
  default: () => [] as AppGroup[],
  transform: appGroups => {
    const filteredAppGroups = appGroups.map(({ name, apps }) => {
      const filteredApps = apps.filter(app => app.show_in_nav);
      sortArrayByProp(filteredApps, app => (app.menu_name || app.name).toUpperCase());
      return { name, apps: filteredApps };
    });
    sortArrayByProp(filteredAppGroups, 'name');
    return filteredAppGroups;
  },
}).data;

const commonListItemProps = {
  class: 'app-menu-item',
  active: false,
};
</script>

<style scoped lang="scss">
@import "~/assets/styles/variables";

.app-menu {
  .app-menu-item {
    a.app-menu-item__append-link {
      &, &:hover, &:visited, &:active, &:focus {
        color: inherit;
      }
    }
  }
}

.app-menu-item {
  min-height: 3.5rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  grid-template-columns: 2rem 1fr auto;

  .v-btn {
    &:hover {
      background-color: color-mix(in srgb, $poppy-red-500 10%, transparent);
    }
  }
}

.app-menu-item__append-link {
  transform: translateX(4px);
  z-index: 5;
}

.app-btn.v-btn {
  height: auto;
  text-align: center;
  text-transform: none;

  .v-btn__content {
    display: block;
  }
}

.app-menu-item--subsection {
  color: $muted-light;
  border-top: 1px solid $faint-line;
  min-height: 2rem;

  &:deep(.v-list-item-title) {
    font-size: inherit;
    font-weight: inherit;
    letter-spacing: inherit;
    line-height: inherit;
    text-transform: inherit;
  }
}
</style>
